import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { quizzesApi } from "@/api";
import type {
  CreateQuizData,
  CreateQuizQuestionData,
  UpdateQuizData,
  UpdateQuizQuestionData,
} from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Dars uchun test (yoki `null`). */
export const useQuiz = (lessonId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_QUIZ, lessonId],
    queryFn: () => quizzesApi.getByLesson(lessonId as number),
    enabled: !!lessonId,
    select: res => res.data,
  });

export const useQuizQuestions = (quizId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_QUIZ_QUESTIONS, quizId],
    queryFn: () => quizzesApi.getQuestions(quizId as number),
    enabled: !!quizId,
    select: res => res.data,
  });

const useInvalidateQuiz = (lessonId: number, quizId?: number, courseId?: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_QUIZ, lessonId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_QUIZ_QUESTIONS, quizId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_READINESS, courseId] });
  };
};

export const useCreateQuiz = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateQuiz(lessonId, undefined, courseId);
  return useMutation({
    mutationFn: (data: CreateQuizData) => quizzesApi.createQuiz(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateQuiz = (lessonId: number, quizId?: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateQuiz(lessonId, quizId, courseId);
  return useMutation({
    mutationFn: (vars: { id: number; data: UpdateQuizData }) =>
      quizzesApi.updateQuiz(vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useCreateQuizQuestion = (lessonId: number, quizId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateQuiz(lessonId, quizId, courseId);
  return useMutation({
    mutationFn: (data: CreateQuizQuestionData) => quizzesApi.createQuestion(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateQuizQuestion = (lessonId: number, quizId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateQuiz(lessonId, quizId, courseId);
  return useMutation({
    mutationFn: (vars: { id: number; data: UpdateQuizQuestionData }) =>
      quizzesApi.updateQuestion(vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteQuizQuestion = (lessonId: number, quizId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateQuiz(lessonId, quizId, courseId);
  return useMutation({
    mutationFn: (id: number) => quizzesApi.removeQuestion(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.DELETED"));
    },
  });
};

/** `questionIds` — testdagi BARCHA savollar yangi tartibda. */
export const useReorderQuizQuestions = (lessonId: number, quizId: number, courseId?: number) => {
  const invalidate = useInvalidateQuiz(lessonId, quizId, courseId);
  return useMutation({
    mutationFn: (questionIds: number[]) => quizzesApi.reorderQuestions(quizId, questionIds),
    onSuccess: invalidate,
  });
};
